import {
  DynamicFieldOperation,
  DynamicFieldType,
} from 'components/DynamicFormField/DynamicFormField';
import { httpClient } from 'providers/clients/httpClient';
import { BlockData } from 'screens/design/components/BlockLibrary/components/BlockManager/BlockEditContext/BlockEditContext';
import {
  BlockSearchData,
  BlockSearchParams,
  AssetOverride,
  PreviewData,
  PreviewRequest,
  BaseUserAccountSummary,
} from 'utility/types/dataProvider';

/**
 * Sort Rule
 */
export type SortRule = {
  /** name of field to sort on */
  field?: string;
  name?: string;
  /** Direction to sort the field by */
  direction?: 'asc' | 'desc' | 'ASC' | 'DESC' | string;
};

/**
 * Core Block Query Rule
 */
export type QueryRule = {
  /** Field name to query on */
  field?: string;
  /** Query operation to perform against the field */
  operation?: string;
  /**
   * Operand of the query against the field
   * string | number | boolean | any[] | Record<string, any>;
   **/
  value?: any;
  /** Label for the selected value (for fields with dynamic sources) */
  value_label?: string | null;
  /** addtional api provided data */
  extra?: {
    screenName?: string;
    [key: string]: any;
  };
  /**
   * NOTE: some additional properties here are added or modified
   * internally in areas like BlockEditWizard or / BlockEditProvider
   * seemingly for mappings to dynamic components.
   *
   * They should be stripped out of the actual block
   * data before sending it back up to the API.
   */
  name?: string;
  id?: string;
};

/**
 * Result Options
 * defaults: start: 0, limit: 10
 */
export type ResultRule = {
  start?: number;
  limit?: number;
};

/**
 * dynamic query field types...
 */
export type TQueryField = {
  field: DynamicFieldType;
  operations: DynamicFieldOperation[];
};

export type TSortField = {
  name: string;
  label: string;
  type: string;
};

export type CoreBlockAppConfig = {
  name?: string;
  label?: string;
  capabilities?: string[];
  query_fields?: {
    // eg: "start_time": { field, operations }; ...
    [key: string]: TQueryField;
  };
  sort_fields?: TSortField[];
  default_query_rules?: QueryRule[];
  default_sort_rules?: SortRule[];
  [key: string]: any;
};

export type BlockConfigAppsDetails = {
  [key: string]: CoreBlockAppConfig;
};

/**
 * GET /core/block/config/ result type
 */
export type CoreBlockConfigResponse = {
  status?: string | number;
  data?: {
    apps?: BlockConfigAppsDetails;
  };
};

/**
 * Perform asset search for content block
 * POST core/block/${map_id}/assets
 * Returns common queryable search results
 */

export type GetBlockAssetSearchArgs = {
  map_id?: string | null;
  limit?: number;
  page?: number;
  bodyData?: {
    block?: BlockData;
    preview?: PreviewRequest;
    /** Include all pinned assets in the results */
    include_all_pinned?: boolean;
    /** ID of URL to optionally search under context of */
    url_id?: string | number;
    [key: string]: any;
  };
};

/**
 * Assets returned via block/queryable search
 * components['schemas']['core.field.queryable.search.results']
 */
export type BlockAssetSearchItem = {
  uuid?: string;
  app?: string;
  archive_time?: string | null;
  authors?: BaseUserAccountSummary[];
  byline?: string;
  draft?: boolean;
  flags?: string[];
  keywords?: string[];
  no_publish?: number;
  previews?: PreviewData;
  start_time?: string;
  summary?: string;
  title?: string;
  type?: string;
};

export type GetBlockAssetSearchResult = {
  status?: string | number;
  data?: {
    items?: BlockAssetSearchItem[];
    next?: string;
    prev?: string;
    total?: number;
  };
};

/**
 * -----------------------------------------------
 * Block Library API Provider
 * -----------------------------------------------
 */
export const blockLibraryProvider = {
  // Search blocks for a URL map
  getBlockSearch: async (
    map_id: string,
    searchParams: BlockSearchParams = {},
    limit: number = 10,
    page: number = 0,
  ): Promise<BlockSearchData> => {
    try {
      const params: Record<string, string> = {
        limit: limit.toString(),
        page: page.toString(),
      };

      // Add optional search parameters if provided
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params[key] = String(value);
        }
      });

      const queryString = new URLSearchParams(params).toString();
      const path = `/core/block/${map_id}/?${queryString}`;

      const { data } = await httpClient({
        url: path,
      });

      return data?.data as BlockSearchData;
    } catch (error) {
      console.error('[getBlockSearch] Error', error);
      throw error;
    }
  },

  // Get block management configuration details
  getBlockConfig: async (): Promise<CoreBlockConfigResponse> => {
    try {
      const url = `/core/block/config/`;

      const { status, data } = await httpClient({
        url: url,
      });

      const result: CoreBlockConfigResponse = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getBlockConfig] Error', error);
      throw error;
    }
  },

  // Create a new block
  createBlock: async (map_id: string, blockData: any) => {
    try {
      const url = `/core/block/${map_id}/`;
      const { data } = await httpClient({
        url: url,
        method: 'POST',
        data: JSON.stringify(blockData),
      });

      return data?.data;
    } catch (error) {
      console.error('[createBlock] Error', error);
      throw error;
    }
  },

  // Retrieve a block by ID
  getBlock: async (map_id: string, block_id: string) => {
    try {
      const url = `/core/block/${map_id}/${block_id}/`;
      const { data } = await httpClient({
        url: url,
      });

      return data?.data;
    } catch (error) {
      console.error('[getBlock] Error', error);
      throw error;
    }
  },

  // Update a block by ID
  updateBlock: async (map_id: string, block_id: string, blockData: any) => {
    try {
      const url = `/core/block/${map_id}/${block_id}/`;
      const { data } = await httpClient({
        url: url,
        method: 'PUT',
        data: JSON.stringify(blockData),
      });

      return data?.data;
    } catch (error) {
      console.error('[updateBlock] Error', error);
      throw error;
    }
  },

  // Delete a block by ID
  deleteBlock: async (map_id: string, block_id: string) => {
    try {
      const url = `/core/block/${map_id}/${block_id}/`;
      const { data } = await httpClient({
        url: url,
        method: 'DELETE',
      });

      return data?.data;
    } catch (error) {
      console.error('[deleteBlock] Error', error);
      throw error;
    }
  },

  // Generate a preview rendering for an anonymous block
  getAnonymousBlockPreview: async (
    map_id: string,
    blockData: Record<string, any>,
    pageData?: { url: string; url_id: number; region: string },
  ) => {
    try {
      const isWrapped =
        typeof blockData === 'object' && blockData !== null && 'block' in blockData;

      // Normalize block structure
      const normalizedBlock = isWrapped
        ? {
            ...blockData.block,
            id: 'id' in blockData.block ? blockData.block.id : 1,
          }
        : {
            ...blockData,
            id: 'id' in blockData ? blockData.id : 1,
          };

      // Final payload
      const payload = {
        block: normalizedBlock,
        url: pageData?.url ?? '/',
        url_id: pageData?.url_id,
        region: pageData?.region ?? 'block-preview-region',
      };

      const { data } = await httpClient({
        url: `/core/block/${map_id}/preview/`,
        method: 'POST',
        data: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/html',
        },
      });

      return data;
    } catch (error) {
      console.error('[getAnonymousBlockPreview] Error', error);
      throw error;
    }
  },

  // Update a block asset override
  updateAssetOverride: async (
    map_id: string,
    block_id: string,
    asset_id: string,
    overrideData: AssetOverride,
  ) => {
    try {
      const url = `/core/block/${map_id}/${block_id}/override/${asset_id}/`;
      const { data } = await httpClient({
        url: url,

        method: 'PUT',
        data: JSON.stringify(overrideData),
      });

      return data;
    } catch (error) {
      console.error('[updateAssetOverride] Error', error);
      throw error;
    }
  },

  // Perform asset search for an anonymous content block
  getBlockAssetSearch: async (
    params: GetBlockAssetSearchArgs,
  ): Promise<GetBlockAssetSearchResult> => {
    try {
      const map_id = params?.map_id;
      const limit = params?.limit ?? 10; // Max allowed in the block
      const page = params?.page ?? 0; // None after 50 for a block
      const body = params?.bodyData ?? {};

      const url = `/core/block/${map_id}/assets/?limit=${limit}&page=${page}`;
      const { data } = await httpClient({
        url: url,
        method: 'POST',
        data: JSON.stringify(body),
      });

      const result: GetBlockAssetSearchResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getBlockAssetSearch] Error', error);
      throw error;
    }
  },
};
